import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import ReactConfetti from 'react-confetti';
import { useAuth } from '../auth/AuthContext';
import { useCompany } from './CompanyContext';
import EosLogo from '../layout/EosLogo';

function CompanyConfig() {
  const [companyName, setCompanyName] = useState('');
  const [industry, setIndustry] = useState('');
  const [size, setSize] = useState('');
  const [description, setDescription] = useState('');
  const [mission, setMission] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  
  // Glass effect states
  const [isVisible, setIsVisible] = useState(false);
  const [blurApplied, setBlurApplied] = useState(false);
  const [backdropVisible, setBackdropVisible] = useState(false);
  const modalRef = useRef(null);
  
  // Celebration modal states
  const [celebrationVisible, setCelebrationVisible] = useState(false);
  const [celebrationMounted, setCelebrationMounted] = useState(false);
  const [celebrationOpacity, setCelebrationOpacity] = useState(false);
  const [celebrationBackdrop, setCelebrationBackdrop] = useState(false);
  const [confettiActive, setConfettiActive] = useState(false);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });
  const celebrationRef = useRef(null);
  
  // Common text shadow style with subtle taper and lower opacity
  const textShadowStyle = { textShadow: '0 1px 1px rgba(0,0,0,0.2), 0 2px 3px rgba(0,0,0,0.1)' };
  // More pronounced shadow for headings
  const headingShadowStyle = { textShadow: '0 1px 2px rgba(0,0,0,0.25), 0 2px 4px rgba(0,0,0,0.15), 0 4px 8px rgba(0,0,0,0.1)' };
  
  const { currentUser } = useAuth();
  const { companyInfo, updateCompanyInfo } = useCompany();
  const navigate = useNavigate();

  // Handle window resize for confetti
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle blur and animations on component mount
  useEffect(() => {
    // First render the component
    let blurTimer, backdropTimer, visibilityTimer;
    
    // Apply blur immediately but keep opacity at 0
    blurTimer = setTimeout(() => {
      setBlurApplied(true);
      
      // Wait for blur to be fully applied before showing backdrop
      backdropTimer = setTimeout(() => {
        setBackdropVisible(true);
        
        // Only after backdrop and blur are ready, fade in content
        visibilityTimer = setTimeout(() => {
          setIsVisible(true);
        }, 100);
      }, 50);
    }, 10);
    
    // Cleanup all timers on unmount
    return () => {
      clearTimeout(blurTimer);
      clearTimeout(backdropTimer);
      clearTimeout(visibilityTimer);
    };
  }, []);
  
  // Handle celebration modal animation
  useEffect(() => {
    let modalTimer, backdropTimer, opacityTimer, unmountTimer, confettiTimer;
    
    if (celebrationVisible) {
      // First mount the component with opacity 0
      setCelebrationMounted(true);
      
      // Apply backdrop blur after mounting
      backdropTimer = setTimeout(() => {
        setCelebrationBackdrop(true);
        
        // Then fade in content
        opacityTimer = setTimeout(() => {
          setCelebrationOpacity(true);
          
          // Start confetti after modal appears
          confettiTimer = setTimeout(() => {
            setConfettiActive(true);
          }, 300);
        }, 200);
      }, 100);
    } else if (celebrationMounted) {
      // First stop confetti
      setConfettiActive(false);
      
      // Fade out content
      setCelebrationOpacity(false);
      
      // After fading out content, fade out backdrop
      opacityTimer = setTimeout(() => {
        setCelebrationBackdrop(false);
        
        // Then unmount the component
        unmountTimer = setTimeout(() => {
          setCelebrationMounted(false);
        }, 300);
      }, 300);
    }
    
    return () => {
      clearTimeout(modalTimer);
      clearTimeout(backdropTimer);
      clearTimeout(opacityTimer);
      clearTimeout(unmountTimer);
      clearTimeout(confettiTimer);
    };
  }, [celebrationVisible]);
  
  // Handle displaying celebration modal
  const openCelebrationModal = () => {
    setCelebrationVisible(true);
  };

  // Handle form submission
  async function handleContinue(e) {
    e.preventDefault();
    
    if (!companyName || !industry || !size) {
      return setError('Please fill out all required fields');
    }
    
    openCelebrationModal();
  }
  
  // Handle actual form submission and navigation
  async function handleSubmitCompany() {
    try {
      setError('');
      setLoading(true);
      
      // Create a unique ID for the company
      const companyId = uuidv4();
      
      // Create company data object
      const companyData = {
        id: companyId,
        name: companyName,
        industry,
        size,
        description,
        mission,
        createdAt: new Date(),
        createdBy: currentUser.uid
      };
      
      // Use updateCompanyInfo to save to Firestore and update user profile
      const success = await updateCompanyInfo(companyData);
      
      // Set loading to false before navigation
      setLoading(false);
      
      if (success) {
        closeCelebrationModal();
        setTimeout(() => {
          navigate('/');
        }, 800); // Wait for animation to complete before navigating
      } else {
        setError('Failed to save company information. Please try again.');
        closeCelebrationModal();
      }
    } catch (err) {
      console.error('Failed to save company information:', err);
      setError('Failed to save company information: ' + err.message);
      setLoading(false);
      closeCelebrationModal();
    }
  }
  
  // Handle closing celebration modal
  const closeCelebrationModal = () => {
    setCelebrationVisible(false);
  };

  return (
    <div className="max-w-md w-full relative z-10 px-4 sm:px-0">
     <div 
  ref={modalRef}
  className={`bg-white bg-opacity-40 border border-white border-opacity-30
  rounded-2xl transition-all duration-500 ease-out transform
  ${isVisible ? 'scale-100 translate-y-0 opacity-100' : 'scale-95 translate-y-4 opacity-0'}`}
  style={{
    boxShadow: blurApplied 
      ? '0 0 50px 20px rgba(255,255,255,0.2), 0 6px 20px rgba(0,0,0,0.1), 0 10px 40px 25px rgba(0,0,0,0.15)' 
      : 'none',
    backdropFilter: 'blur(14px)',
    background: 'linear-gradient(135deg, rgba(200,200,200,0.35) 0%, rgba(180,180,180,0.25) 100%)'
  }}
>

        <div className="p-4 sm:p-8">
          <div className="flex justify-center mb-6 sm:mb-8">
            <div className="transition-transform duration-300 transform hover:scale-110 w-36 sm:w-44 h-18 sm:h-24 flex items-center justify-center p-1 bg-white bg-opacity-20 rounded-lg shadow-sm">
              <EosLogo customLogoUrl={companyInfo?.customLogoUrl} />
            </div>
          </div>
          
          <h2 className="text-center text-xl sm:text-2xl font-bold text-white mb-6 sm:mb-8 font-heading" style={headingShadowStyle}>
            Configure Your Company
          </h2>
          
          {error && (
            <div className="bg-red-50 bg-opacity-70 border-l-4 border-red-500 text-red-700 p-3 mb-6 rounded-r-md shadow-sm animate-fade-in" role="alert">
              <p className="font-bold font-heading">Error</p>
              <p>{error}</p>
            </div>
          )}
          
          <form className="space-y-4 sm:space-y-6" onSubmit={handleContinue}>
            <div className="transition-all duration-300 hover:bg-white hover:bg-opacity-20 p-2 sm:p-3 rounded-md -mx-2 sm:-mx-3 group">
              <label htmlFor="company-name" className="block text-sm font-medium text-white mb-1 font-heading group-hover:text-gray-500" style={textShadowStyle}>
                Company Name <span className="text-red-500">*</span>
              </label>
              <input
                id="company-name"
                name="company-name"
                type="text"
                required
                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 
                  placeholder-white text-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 
                  focus:border-orange-500 sm:text-sm transition-all duration-300 bg-white bg-opacity-30"
                placeholder="Acme Inc."
                style={textShadowStyle}
                value={companyName}
                onChange={e => setCompanyName(e.target.value)}
              />
            </div>
            
            <div className="transition-all duration-300 hover:bg-white hover:bg-opacity-20 p-2 sm:p-3 rounded-md -mx-2 sm:-mx-3 group">
              <label htmlFor="industry" className="block text-sm font-medium text-white mb-1 font-heading group-hover:text-gray-500" style={textShadowStyle}>
                Industry <span className="text-red-500">*</span>
              </label>
              <select
                id="industry"
                name="industry"
                required
                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 
                  placeholder-white text-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 
                  focus:border-orange-500 sm:text-sm transition-all duration-300 bg-white bg-opacity-30"
                value={industry}
                onChange={e => setIndustry(e.target.value)}
              >
                <option value="">Select Industry</option>
                <option value="technology">Technology</option>
                <option value="healthcare">Healthcare</option>
                <option value="finance">Finance</option>
                <option value="retail">Retail</option>
                <option value="manufacturing">Manufacturing</option>
                <option value="education">Education</option>
                <option value="other">Other</option>
              </select>
            </div>
            
            <div className="transition-all duration-300 hover:bg-white hover:bg-opacity-20 p-2 sm:p-3 rounded-md -mx-2 sm:-mx-3 group">
              <label htmlFor="size" className="block text-sm font-medium text-white mb-1 font-heading group-hover:text-gray-500" style={textShadowStyle}>
                Company Size <span className="text-red-500">*</span>
              </label>
              <select
                id="size"
                name="size"
                required
                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 
                  placeholder-white text-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 
                  focus:border-orange-500 sm:text-sm transition-all duration-300 bg-white bg-opacity-30"
                value={size}
                onChange={e => setSize(e.target.value)}
              >
                <option value="">Select Size</option>
                <option value="1-10">1-10 employees</option>
                <option value="11-50">11-50 employees</option>
                <option value="51-200">51-200 employees</option>
                <option value="201-500">201-500 employees</option>
                <option value="501-1000">501-1000 employees</option>
                <option value="1000+">1000+ employees</option>
              </select>
            </div>
            
            <div className="transition-all duration-300 hover:bg-white hover:bg-opacity-20 p-2 sm:p-3 rounded-md -mx-2 sm:-mx-3 group">
              <label htmlFor="description" className="block text-sm font-medium text-white mb-1 font-heading group-hover:text-gray-500" style={textShadowStyle}>
                Description (optional)
              </label>
              <textarea
                id="description"
                name="description"
                rows="3"
                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 
                  placeholder-white text-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 
                  focus:border-orange-500 sm:text-sm transition-all duration-300 bg-white bg-opacity-30"
                placeholder="Briefly describe your company..."
                style={textShadowStyle}
                value={description}
                onChange={e => setDescription(e.target.value)}
              />
            </div>
            
            <div className="transition-all duration-300 hover:bg-white hover:bg-opacity-20 p-2 sm:p-3 rounded-md -mx-2 sm:-mx-3 group">
              <label htmlFor="mission" className="block text-sm font-medium text-white mb-1 font-heading group-hover:text-gray-500" style={textShadowStyle}>
                Mission Statement (optional)
              </label>
              <textarea
                id="mission"
                name="mission"
                rows="3"
                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 
                  placeholder-white text-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 
                  focus:border-orange-500 sm:text-sm transition-all duration-300 bg-white bg-opacity-30"
                placeholder="What's your company's mission?"
                style={textShadowStyle}
                value={mission}
                onChange={e => setMission(e.target.value)}
              />
            </div>
            
            <div className="pt-4 sm:pt-6">
              <button
                type="submit"
                disabled={loading}
                className="group w-full flex justify-center py-2 sm:py-3 px-4 border border-transparent text-base sm:text-md font-medium rounded-modern text-white bg-orange-500 hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-all duration-300 shadow-eos-orange hover:shadow-lg transform hover:-translate-y-0.5"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </>
                ) : (
                  <span className="flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 group-hover:translate-x-0.5 transition-transform duration-300" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                    Continue
                  </span>
                )}
              </button>
            </div>
          </form>
          
          <div className="mt-8 pt-4 border-t border-white border-opacity-20 text-center">
            <p className="text-xs text-white text-opacity-70 mt-4 animate-pulse-slow" style={textShadowStyle}>
              Powered by the Entrepreneurial Operating System®
            </p>
          </div>
        </div>
      </div>
      
      {/* Celebration Modal - shows after form submission */}
      {celebrationMounted && (
        <div 
          ref={celebrationRef}
          className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity duration-300 ${celebrationOpacity ? 'opacity-100' : 'opacity-0'}`}
        >
          {/* Confetti */}
          {confettiActive && (
            <ReactConfetti
              width={windowSize.width}
              height={windowSize.height}
              recycle={false}
              numberOfPieces={200}
              gravity={0.2}
              colors={['#FF8200', '#0045A7', '#e36a00', '#003880', '#FBC533']}
            />
          )}
          
          {/* Modal backdrop */}
          <div 
            className={`fixed inset-0 backdrop-filter transition-all duration-500 ${celebrationBackdrop ? 'backdrop-blur-md bg-gray-900 bg-opacity-50' : 'backdrop-blur-none bg-opacity-0'}`}
            onClick={closeCelebrationModal}
          ></div>
          
          {/* Modal content */}
          <div 
            className={`bg-white rounded-modern shadow-card relative transform transition-all duration-500 ${celebrationOpacity ? 'scale-100' : 'scale-90'} w-full max-w-md mx-auto p-6 sm:p-8`}
          >
            <div className="text-center">
              <div className="bg-orange-100 rounded-full p-3 mx-auto w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 sm:h-12 sm:w-12 text-orange-500 animate-pulse-slow" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              
              <h3 className="text-xl sm:text-2xl leading-7 font-bold text-gray-900 font-heading mb-2">
                Congratulations!
              </h3>
              <p className="text-gray-500 mb-6 text-base sm:text-lg">
                Your company has been created successfully. You're ready to begin!
              </p>
              
              <button
                onClick={handleSubmitCompany}
                disabled={loading}
                className="w-full inline-flex justify-center items-center px-4 py-3 sm:py-4 border border-transparent text-base sm:text-lg font-medium rounded-modern text-white bg-orange-500 hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 shadow-eos-orange hover:shadow-lg transition-all duration-300"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </>
                ) : (
                  <span className="flex items-center">
                    Continue to Dashboard
                    <svg xmlns="http://www.w3.org/2000/svg" className="ml-2 h-5 w-5 transform group-hover:translate-x-1 transition-transform duration-300" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CompanyConfig;